import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Plus,
  Search,
  Filter,
  FileText,
  Send,
  Check,
  X,
  Loader2,
  Package,
  Truck,
  DollarSign,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Eye,
  Copy,
  Receipt,
  Building2,
  Phone,
  Mail,
  MapPin,
  Calendar,
  MoreVertical,
  Edit3,
  Trash2,
  CheckCircle2,
  XCircle,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// ================= HELPER FUNCTIONS =================

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "TBD";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const getStatusConfig = (status) => {
  const configs = {
    draft: { label: 'Draft', bg: 'bg-slate-500/20', text: 'text-slate-400', icon: FileText },
    pending_approval: { label: 'Pending Approval', bg: 'bg-amber-500/20', text: 'text-amber-400', icon: Clock },
    sent: { label: 'Sent', bg: 'bg-blue-500/20', text: 'text-blue-400', icon: Send },
    acknowledged: { label: 'Acknowledged', bg: 'bg-indigo-500/20', text: 'text-indigo-400', icon: CheckCircle2 },
    partial_received: { label: 'Partial Received', bg: 'bg-purple-500/20', text: 'text-purple-400', icon: Package },
    received: { label: 'Received', bg: 'bg-teal-500/20', text: 'text-teal-400', icon: Truck },
    paid: { label: 'Paid', bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: Check },
    cancelled: { label: 'Cancelled', bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle },
    overdue: { label: 'Overdue', bg: 'bg-red-500/20', text: 'text-red-400', icon: AlertTriangle },
  };
  return configs[status] || configs.draft;
};

// ================= STATUS BADGE =================

const POStatusBadge = ({ status }) => {
  const config = getStatusConfig(status);
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wider ${config.bg} ${config.text}`}>
      <Icon className="h-3.5 w-3.5" />
      {config.label}
    </span>
  );
};

// ================= CREATE PO MODAL =================

const CreatePOModal = ({ projectId, milestones, vendors, onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [vendor, setVendor] = useState({
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: { street: '', city: '', state: '', zip: '' }
  });
  const [reason, setReason] = useState('');
  const [milestoneId, setMilestoneId] = useState('');
  const [costCategory, setCostCategory] = useState('materials');
  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, unit: 'ea', unit_price: 0 }
  ]);
  const [taxRate, setTaxRate] = useState(0);
  const [shipping, setShipping] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [deliveryMethod, setDeliveryMethod] = useState('delivery');
  const [neededBy, setNeededBy] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('net_30');
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [notes, setNotes] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  
  const costCategories = [
    { value: 'materials', label: 'Materials' },
    { value: 'labor', label: 'Labor' },
    { value: 'equipment', label: 'Equipment Rental' },
    { value: 'permits', label: 'Permits & Fees' },
    { value: 'subcontractor', label: 'Subcontractor' },
    { value: 'overhead', label: 'Overhead' },
    { value: 'other', label: 'Other' },
  ];
  
  const paymentTermsOptions = [
    { value: 'cod', label: 'COD (Cash on Delivery)' },
    { value: 'net_15', label: 'Net 15' },
    { value: 'net_30', label: 'Net 30' },
    { value: 'net_45', label: 'Net 45' },
    { value: 'on_account', label: 'On Account' },
    { value: 'prepaid', label: 'Prepaid' },
  ];
  
  const handleVendorSelect = (v) => {
    setSelectedVendor(v);
    setVendor({
      vendor_id: v.vendor_id,
      name: v.name,
      contact_name: v.contact_name || '',
      email: v.email || '',
      phone: v.phone || '',
      address: v.address || { street: '', city: '', state: '', zip: '' }
    });
  };
  
  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unit: 'ea', unit_price: 0 }]);
  };
  
  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };
  
  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    setLineItems(updated);
  };
  
  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };
  
  const calculateTax = () => {
    return calculateSubtotal() * (taxRate / 100);
  };
  
  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax() + shipping - discount;
  };
  
  const handleSubmit = async () => {
    if (!vendor.name) {
      toast.error("Please select or enter a vendor");
      return;
    }
    if (!reason || reason.length < 10) {
      toast.error("Please provide a reason (minimum 10 characters)");
      return;
    }
    if (lineItems.length === 0 || !lineItems[0].description) {
      toast.error("Please add at least one line item");
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        vendor,
        reason_description: reason,
        milestone_id: milestoneId || null,
        cost_category: costCategory,
        line_items: lineItems.filter(item => item.description),
        tax_rate: taxRate,
        shipping,
        discount,
        delivery: {
          method: deliveryMethod,
          address: deliveryMethod === 'delivery' ? vendor.address : null,
          instructions: deliveryInstructions,
          needed_by: neededBy || null,
        },
        payment: {
          terms: paymentTerms,
        },
        requires_approval: requiresApproval,
        notes: notes || null
      };
      
      await axios.post(
        `${API_BASE}/api/purchase-orders/project/${projectId}`,
        payload,
        { withCredentials: true }
      );
      
      toast.success("Purchase order created!");
      onCreated();
    } catch (error) {
      console.error("Error creating PO:", error);
      toast.error(error.response?.data?.detail || "Failed to create purchase order");
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-3xl rounded-xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <FileText className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Create Purchase Order</h3>
              <p className="text-sm text-slate-400">Step {step} of 3</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Progress Bar */}
        <div className="px-6 py-3 border-b border-slate-800/50 bg-slate-900/30">
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  s < step ? 'bg-emerald-500 text-white' :
                  s === step ? 'bg-emerald-500/20 text-emerald-400 border-2 border-emerald-500' :
                  'bg-slate-800 text-slate-500'
                }`}>
                  {s < step ? <Check className="h-4 w-4" /> : s}
                </div>
                {s < 3 && (
                  <div className={`flex-1 h-1 rounded ${s < step ? 'bg-emerald-500' : 'bg-slate-800'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-slate-500">
            <span>Vendor & Reason</span>
            <span>Line Items</span>
            <span>Delivery & Payment</span>
          </div>
        </div>
        
        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Vendor & Reason */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Saved Vendors */}
              {vendors && vendors.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Select Saved Vendor</label>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                    {vendors.map((v) => (
                      <button
                        key={v.vendor_id}
                        onClick={() => handleVendorSelect(v)}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          selectedVendor?.vendor_id === v.vendor_id
                            ? 'border-emerald-500 bg-emerald-500/10'
                            : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                        }`}
                      >
                        <p className="font-medium text-white truncate">{v.name}</p>
                        <p className="text-xs text-slate-400 truncate">{v.email || v.phone || 'No contact'}</p>
                      </button>
                    ))}
                  </div>
                  <div className="text-center py-2 text-slate-500 text-sm">- or enter new vendor -</div>
                </div>
              )}
              
              {/* Vendor Name */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Vendor Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={vendor.name}
                  onChange={(e) => setVendor({ ...vendor, name: e.target.value })}
                  placeholder="ABC Supply Co."
                  className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
                  data-testid="po-vendor-name-input"
                />
              </div>
              
              {/* Vendor Contact Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Contact Name</label>
                  <input
                    type="text"
                    value={vendor.contact_name}
                    onChange={(e) => setVendor({ ...vendor, contact_name: e.target.value })}
                    placeholder="John Smith"
                    className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                  <input
                    type="email"
                    value={vendor.email}
                    onChange={(e) => setVendor({ ...vendor, email: e.target.value })}
                    placeholder="vendor@email.com"
                    className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Phone</label>
                <input
                  type="tel"
                  value={vendor.phone}
                  onChange={(e) => setVendor({ ...vendor, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
                />
              </div>
              
              {/* Reason (Required) */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Reason for Purchase <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Describe why this purchase is needed for the project (minimum 10 characters)..."
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 resize-none placeholder-slate-500"
                  data-testid="po-reason-input"
                />
                <p className="text-xs text-slate-500 mt-1">{reason.length}/10 minimum characters</p>
              </div>
              
              {/* Cost Category & Milestone */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Cost Category</label>
                  <select
                    value={costCategory}
                    onChange={(e) => setCostCategory(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                  >
                    {costCategories.map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Link to Milestone</label>
                  <select
                    value={milestoneId}
                    onChange={(e) => setMilestoneId(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                  >
                    <option value="">No milestone</option>
                    {milestones.map((m) => (
                      <option key={m.milestone_id} value={m.milestone_id}>{m.title}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 2: Line Items */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="space-y-3">
                {lineItems.map((item, idx) => (
                  <div key={idx} className="p-4 rounded-lg bg-slate-800/50 border border-slate-700 space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-400">
                        {idx + 1}
                      </span>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                          placeholder="Item description"
                          className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
                          data-testid={`po-line-item-${idx}-description`}
                        />
                      </div>
                      {lineItems.length > 1 && (
                        <button 
                          onClick={() => removeLineItem(idx)}
                          className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-all"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-3 ml-9">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Quantity</label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Unit</label>
                        <select
                          value={item.unit}
                          onChange={(e) => updateLineItem(idx, 'unit', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                        >
                          <option value="ea">Each</option>
                          <option value="sf">Sq Ft</option>
                          <option value="lf">Lin Ft</option>
                          <option value="cy">Cu Yd</option>
                          <option value="ton">Ton</option>
                          <option value="gal">Gallon</option>
                          <option value="box">Box</option>
                          <option value="bag">Bag</option>
                          <option value="ls">Lump Sum</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Unit Price</label>
                        <input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => updateLineItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Total</label>
                        <div className="px-3 py-2 rounded-lg bg-slate-900/50 border border-slate-700/50 text-emerald-400 font-medium">
                          {formatCurrency(item.quantity * item.unit_price)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                onClick={addLineItem}
                className="w-full py-3 rounded-lg border-2 border-dashed border-slate-700 text-slate-400 hover:border-emerald-500 hover:text-emerald-400 transition-all flex items-center justify-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Line Item
              </button>
              
              {/* Totals */}
              <div className="p-4 rounded-lg bg-slate-800 border border-slate-700">
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Tax Rate (%)</label>
                    <input
                      type="number"
                      value={taxRate}
                      onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                      min="0"
                      step="0.1"
                      className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Shipping</label>
                    <input
                      type="number"
                      value={shipping}
                      onChange={(e) => setShipping(parseFloat(e.target.value) || 0)}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Discount</label>
                    <input
                      type="number"
                      value={discount}
                      onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>
                <div className="space-y-2 border-t border-slate-700 pt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Subtotal</span>
                    <span className="text-white">{formatCurrency(calculateSubtotal())}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Tax ({taxRate}%)</span>
                    <span className="text-white">{formatCurrency(calculateTax())}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Shipping</span>
                    <span className="text-white">{formatCurrency(shipping)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Discount</span>
                      <span className="text-red-400">-{formatCurrency(discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold border-t border-slate-700 pt-2">
                    <span className="text-white">Total</span>
                    <span className="text-emerald-400">{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 3: Delivery & Payment */}
          {step === 3 && (
            <div className="space-y-6">
              {/* Delivery */}
              <div>
                <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Truck className="h-4 w-4 text-emerald-400" />
                  Delivery Details
                </h4>
                
                <div className="space-y-4">
                  <div className="flex gap-3">
                    {['delivery', 'pickup', 'will_call'].map((method) => (
                      <button
                        key={method}
                        onClick={() => setDeliveryMethod(method)}
                        className={`flex-1 py-3 rounded-lg border font-medium transition-all ${
                          deliveryMethod === method
                            ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                            : 'border-slate-700 text-slate-400 hover:border-slate-600'
                        }`}
                      >
                        {method === 'delivery' && 'Delivery'}
                        {method === 'pickup' && 'Pickup'}
                        {method === 'will_call' && 'Will Call'}
                      </button>
                    ))}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Needed By Date</label>
                    <input
                      type="date"
                      value={neededBy}
                      onChange={(e) => setNeededBy(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Delivery Instructions</label>
                    <textarea
                      value={deliveryInstructions}
                      onChange={(e) => setDeliveryInstructions(e.target.value)}
                      placeholder="Gate code, contact on site, special handling..."
                      rows={2}
                      className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 resize-none placeholder-slate-500"
                    />
                  </div>
                </div>
              </div>
              
              {/* Payment */}
              <div>
                <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-emerald-400" />
                  Payment Terms
                </h4>
                
                <div className="space-y-4">
                  <select
                    value={paymentTerms}
                    onChange={(e) => setPaymentTerms(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
                  >
                    {paymentTermsOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  
                  <label className="flex items-center gap-3 p-4 rounded-lg bg-slate-800/50 border border-slate-700 cursor-pointer hover:border-slate-600 transition-all">
                    <input
                      type="checkbox"
                      checked={requiresApproval}
                      onChange={(e) => setRequiresApproval(e.target.checked)}
                      className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
                    />
                    <div>
                      <p className="font-medium text-white">Requires Approval</p>
                      <p className="text-sm text-slate-400">PO will need approval before it can be sent to vendor</p>
                    </div>
                  </label>
                </div>
              </div>
              
              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Additional Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional information for this purchase order..."
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 resize-none placeholder-slate-500"
                />
              </div>
              
              {/* Summary */}
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <h4 className="font-bold text-emerald-400 mb-2">Order Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-400">Vendor:</span>
                    <span className="text-white ml-2">{vendor.name || 'Not specified'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Items:</span>
                    <span className="text-white ml-2">{lineItems.filter(i => i.description).length}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Total:</span>
                    <span className="text-emerald-400 font-bold ml-2">{formatCurrency(calculateTotal())}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Payment:</span>
                    <span className="text-white ml-2">{paymentTermsOptions.find(p => p.value === paymentTerms)?.label}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/50 flex justify-between flex-shrink-0">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 font-medium transition-all"
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          
          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && (!vendor.name || reason.length < 10)}
              className="px-6 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              Continue
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-6 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-all disabled:opacity-50 flex items-center gap-2"
              data-testid="po-create-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {submitting ? 'Creating...' : 'Create Purchase Order'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ================= PO DETAIL MODAL =================

const PODetailModal = ({ po, onClose, onAction, onRefresh }) => {
  const [activeDetailTab, setActiveDetailTab] = useState('details');
  const [actionLoading, setActionLoading] = useState(null);
  
  const handleAction = async (action, data = {}) => {
    setActionLoading(action);
    try {
      await onAction(action, po.po_id, data);
    } finally {
      setActionLoading(null);
    }
  };
  
  if (!po) return null;
  
  const statusConfig = getStatusConfig(po.status);
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-4xl rounded-xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <FileText className="h-6 w-6 text-emerald-400" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-bold text-white">{po.po_number}</h3>
                <POStatusBadge status={po.status} />
              </div>
              <p className="text-sm text-slate-400">{po.vendor?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={onRefresh}
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-all"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="px-6 border-b border-slate-800 flex gap-4">
          {['details', 'items', 'activity'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveDetailTab(tab)}
              className={`py-3 text-sm font-medium border-b-2 transition-all capitalize ${
                activeDetailTab === tab
                  ? 'text-emerald-400 border-emerald-400'
                  : 'text-slate-400 border-transparent hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        
        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeDetailTab === 'details' && (
            <div className="grid grid-cols-2 gap-6">
              {/* Left Column - Vendor & Reason */}
              <div className="space-y-6">
                <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Vendor
                  </h4>
                  <p className="text-lg font-bold text-white">{po.vendor?.name}</p>
                  {po.vendor?.contact_name && (
                    <p className="text-sm text-slate-400">Attn: {po.vendor.contact_name}</p>
                  )}
                  {po.vendor?.email && (
                    <p className="text-sm text-slate-400 flex items-center gap-1 mt-2">
                      <Mail className="h-3 w-3" /> {po.vendor.email}
                    </p>
                  )}
                  {po.vendor?.phone && (
                    <p className="text-sm text-slate-400 flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {po.vendor.phone}
                    </p>
                  )}
                </div>
                
                <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Reason</h4>
                  <p className="text-white">{po.reason?.description}</p>
                  <div className="flex gap-2 mt-3">
                    <span className="px-2 py-1 rounded text-xs bg-slate-700 text-slate-300 capitalize">
                      {po.reason?.cost_category?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                
                {/* Delivery Info */}
                <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Truck className="h-4 w-4" />
                    Delivery
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Method</span>
                      <span className="text-white capitalize">{po.delivery?.method?.replace('_', ' ')}</span>
                    </div>
                    {po.delivery?.needed_by && (
                      <div className="flex justify-between">
                        <span className="text-slate-400">Needed By</span>
                        <span className="text-white">{formatDate(po.delivery.needed_by)}</span>
                      </div>
                    )}
                    {po.delivery?.instructions && (
                      <div className="mt-2 pt-2 border-t border-slate-700">
                        <span className="text-slate-400 text-xs">Instructions:</span>
                        <p className="text-white text-sm mt-1">{po.delivery.instructions}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Right Column - Financials */}
              <div className="space-y-6">
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <h4 className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-3">Financial Summary</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Subtotal</span>
                      <span className="text-white">{formatCurrency(po.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Tax ({po.tax_rate}%)</span>
                      <span className="text-white">{formatCurrency(po.tax_amount)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Shipping</span>
                      <span className="text-white">{formatCurrency(po.shipping)}</span>
                    </div>
                    {po.discount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Discount</span>
                        <span className="text-red-400">-{formatCurrency(po.discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold border-t border-emerald-500/30 pt-2 mt-2">
                      <span className="text-white">Total</span>
                      <span className="text-emerald-400">{formatCurrency(po.total)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Payment Info */}
                <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Payment
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Terms</span>
                      <span className="text-white capitalize">{po.payment?.terms?.replace('_', ' ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Total Paid</span>
                      <span className="text-emerald-400">{formatCurrency(po.payment?.total_paid || 0)}</span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span className="text-slate-400">Balance Due</span>
                      <span className={po.payment?.balance_due > 0 ? 'text-amber-400' : 'text-emerald-400'}>
                        {formatCurrency(po.payment?.balance_due || 0)}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Dates */}
                <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Timeline
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Created</span>
                      <span className="text-white">{formatDate(po.created_at)}</span>
                    </div>
                    {po.sent_at && (
                      <div className="flex justify-between">
                        <span className="text-slate-400">Sent</span>
                        <span className="text-white">{formatDate(po.sent_at)}</span>
                      </div>
                    )}
                    {po.acknowledged_at && (
                      <div className="flex justify-between">
                        <span className="text-slate-400">Acknowledged</span>
                        <span className="text-white">{formatDate(po.acknowledged_at)}</span>
                      </div>
                    )}
                    {po.receiving?.received_date && (
                      <div className="flex justify-between">
                        <span className="text-slate-400">Received</span>
                        <span className="text-white">{formatDate(po.receiving.received_date)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeDetailTab === 'items' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-700 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Item</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-slate-400 uppercase tracking-wider">Qty</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-slate-400 uppercase tracking-wider">Unit</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-slate-400 uppercase tracking-wider">Unit Price</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-slate-400 uppercase tracking-wider">Total</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-slate-400 uppercase tracking-wider">Received</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/50">
                    {po.line_items?.map((item, idx) => (
                      <tr key={idx} className="hover:bg-slate-800/30">
                        <td className="px-4 py-3 text-sm text-white">{item.description}</td>
                        <td className="px-4 py-3 text-sm text-center text-slate-300">{item.quantity}</td>
                        <td className="px-4 py-3 text-sm text-center text-slate-400">{item.unit}</td>
                        <td className="px-4 py-3 text-sm text-right text-slate-300">{formatCurrency(item.unit_price)}</td>
                        <td className="px-4 py-3 text-sm text-right text-white font-medium">{formatCurrency(item.total)}</td>
                        <td className="px-4 py-3 text-center">
                          {item.received_status === 'complete' ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                              <Check className="h-3 w-3" /> {item.received_qty}/{item.quantity}
                            </span>
                          ) : item.received_qty > 0 ? (
                            <span className="text-xs text-amber-400">
                              {item.received_qty}/{item.quantity}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-500">Pending</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {activeDetailTab === 'activity' && (
            <div className="space-y-4">
              {po.activities?.length > 0 ? (
                <div className="space-y-3">
                  {po.activities.slice().reverse().map((activity, idx) => (
                    <div key={idx} className="flex gap-3 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        activity.type === 'created' ? 'bg-blue-500/20 text-blue-400' :
                        activity.type === 'sent' ? 'bg-indigo-500/20 text-indigo-400' :
                        activity.type === 'acknowledged' ? 'bg-purple-500/20 text-purple-400' :
                        activity.type === 'received' ? 'bg-teal-500/20 text-teal-400' :
                        activity.type === 'payment' ? 'bg-emerald-500/20 text-emerald-400' :
                        activity.type === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                        'bg-slate-700 text-slate-400'
                      }`}>
                        {activity.type === 'created' && <FileText className="h-4 w-4" />}
                        {activity.type === 'sent' && <Send className="h-4 w-4" />}
                        {activity.type === 'acknowledged' && <CheckCircle2 className="h-4 w-4" />}
                        {activity.type === 'received' && <Package className="h-4 w-4" />}
                        {activity.type === 'payment' && <DollarSign className="h-4 w-4" />}
                        {activity.type === 'cancelled' && <XCircle className="h-4 w-4" />}
                        {!['created', 'sent', 'acknowledged', 'received', 'payment', 'cancelled'].includes(activity.type) && (
                          <Clock className="h-4 w-4" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white">{activity.description}</p>
                        <p className="text-xs text-slate-500">{formatDate(activity.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No activity recorded</p>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer - Action Buttons */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/50 flex justify-between flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 font-medium transition-all"
          >
            Close
          </button>
          
          <div className="flex items-center gap-2">
            {po.status === 'draft' && (
              <>
                <button
                  onClick={() => handleAction('send')}
                  disabled={actionLoading === 'send' || !po.vendor?.email}
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {actionLoading === 'send' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send to Vendor
                </button>
                <button
                  onClick={() => handleAction('cancel')}
                  disabled={actionLoading === 'cancel'}
                  className="px-4 py-2 rounded-lg bg-red-600/20 hover:bg-red-600/30 text-red-400 font-medium transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {actionLoading === 'cancel' ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                  Cancel
                </button>
              </>
            )}
            
            {po.status === 'sent' && (
              <button
                onClick={() => handleAction('acknowledge')}
                disabled={actionLoading === 'acknowledge'}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading === 'acknowledge' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Mark Acknowledged
              </button>
            )}
            
            {['sent', 'acknowledged', 'partial_received'].includes(po.status) && (
              <button
                onClick={() => handleAction('receive')}
                disabled={actionLoading === 'receive'}
                className="px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-500 text-white font-medium transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading === 'receive' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
                Receive Items
              </button>
            )}
            
            {['received', 'partial_received'].includes(po.status) && (
              <button
                onClick={() => handleAction('pay')}
                disabled={actionLoading === 'pay'}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading === 'pay' ? <Loader2 className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4" />}
                Record Payment
              </button>
            )}
            
            <button
              onClick={() => handleAction('duplicate')}
              disabled={actionLoading === 'duplicate'}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-medium transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {actionLoading === 'duplicate' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
              Duplicate
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ================= RECEIVE ITEMS MODAL =================

const ReceiveItemsModal = ({ po, onClose, onReceived }) => {
  const [items, setItems] = useState(
    (po?.line_items || []).map(item => ({
      line_id: item.line_id,
      qty_received: item.quantity,
      status: 'complete',
      notes: ''
    }))
  );
  const [receivedBy, setReceivedBy] = useState('');
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const updateItem = (lineId, field, value) => {
    setItems(items.map(item => 
      item.line_id === lineId ? { ...item, [field]: value } : item
    ));
  };
  
  const handleSubmit = async () => {
    if (!receivedBy) {
      toast.error("Please enter who received the items");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/api/purchase-orders/${po.po_id}/receive`,
        {
          items,
          received_by: receivedBy,
          received_date: receivedDate,
          delivery_photos: [],
          notes
        },
        { withCredentials: true }
      );
      
      toast.success("Items received!");
      onReceived();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to receive items");
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-2xl rounded-xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
              <Package className="h-5 w-5 text-teal-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Receive Items</h3>
              <p className="text-sm text-slate-400">{po?.po_number}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Received By</label>
              <input
                type="text"
                value={receivedBy}
                onChange={(e) => setReceivedBy(e.target.value)}
                placeholder="Your name"
                className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-teal-500 placeholder-slate-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Date Received</label>
              <input
                type="date"
                value={receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-teal-500"
              />
            </div>
          </div>
          
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Items</h4>
            {po?.line_items?.map((item, idx) => {
              const receiveItem = items.find(i => i.line_id === item.line_id);
              return (
                <div key={idx} className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium text-white">{item.description}</p>
                      <p className="text-sm text-slate-400">Ordered: {item.quantity} {item.unit}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Qty Received</label>
                      <input
                        type="number"
                        value={receiveItem?.qty_received || 0}
                        onChange={(e) => updateItem(item.line_id, 'qty_received', parseFloat(e.target.value) || 0)}
                        max={item.quantity}
                        min={0}
                        className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Status</label>
                      <select
                        value={receiveItem?.status || 'complete'}
                        onChange={(e) => updateItem(item.line_id, 'status', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-teal-500"
                      >
                        <option value="complete">Complete</option>
                        <option value="partial">Partial</option>
                        <option value="damaged">Damaged</option>
                        <option value="wrong_item">Wrong Item</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Notes</label>
                      <input
                        type="text"
                        value={receiveItem?.notes || ''}
                        onChange={(e) => updateItem(item.line_id, 'notes', e.target.value)}
                        placeholder="Optional"
                        className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:border-teal-500 placeholder-slate-600"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">General Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes about this delivery..."
              rows={2}
              className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-teal-500 resize-none placeholder-slate-500"
            />
          </div>
        </div>
        
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/50 flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 font-medium transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !receivedBy}
            className="px-6 py-2 rounded-lg bg-teal-600 hover:bg-teal-500 text-white font-bold transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {submitting ? 'Saving...' : 'Confirm Receipt'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ================= RECORD PAYMENT MODAL =================

const RecordPOPaymentModal = ({ po, onClose, onPaid }) => {
  const [amount, setAmount] = useState(po?.payment?.balance_due || po?.total || 0);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [method, setMethod] = useState('check');
  const [reference, setReference] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const handleSubmit = async () => {
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/api/purchase-orders/${po.po_id}/pay`,
        {
          amount,
          date,
          method,
          reference,
          invoice_number: invoiceNumber,
          notes
        },
        { withCredentials: true }
      );
      
      toast.success("Payment recorded!");
      onPaid();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md rounded-xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <DollarSign className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Record Payment</h3>
              <p className="text-sm text-slate-400">{po?.po_number}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">PO Total</span>
              <span className="text-white font-bold">{formatCurrency(po?.total)}</span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-slate-400">Previously Paid</span>
              <span className="text-emerald-400">{formatCurrency(po?.payment?.total_paid || 0)}</span>
            </div>
            <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-700">
              <span className="text-slate-400">Balance Due</span>
              <span className="text-amber-400 font-bold">{formatCurrency(po?.payment?.balance_due || po?.total)}</span>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Payment Amount</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
              min="0"
              step="0.01"
              className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Method</label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
              >
                <option value="check">Check</option>
                <option value="ach">ACH / Bank Transfer</option>
                <option value="credit_card">Credit Card</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Reference / Check #</label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Check number or reference"
              className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Vendor Invoice #</label>
            <input
              type="text"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="Vendor's invoice number"
              className="w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
            />
          </div>
        </div>
        
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 font-medium transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !amount}
            className="px-6 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {submitting ? 'Recording...' : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ================= MAIN COMPONENT =================

const PurchaseOrdersTab = ({ projectId, milestones }) => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [receiveModal, setReceiveModal] = useState(null);
  const [paymentModal, setPaymentModal] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const loadPurchaseOrders = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const [posRes, vendorsRes] = await Promise.all([
        axios.get(`${API_BASE}/api/purchase-orders/project/${projectId}`, { withCredentials: true }),
        axios.get(`${API_BASE}/api/purchase-orders/vendors/list`, { withCredentials: true }).catch(() => ({ data: { vendors: [], recent_vendors: [] } }))
      ]);
      
      setPurchaseOrders(posRes.data.purchase_orders || []);
      setStats(posRes.data.stats);
      setVendors([...(vendorsRes.data.vendors || []), ...(vendorsRes.data.recent_vendors || [])]);
    } catch (error) {
      console.error("Error loading purchase orders:", error);
      toast.error("Failed to load purchase orders");
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  
  useEffect(() => {
    loadPurchaseOrders();
  }, [loadPurchaseOrders]);
  
  const handlePOAction = async (action, poId, data = {}) => {
    try {
      let endpoint = `${API_BASE}/api/purchase-orders/${poId}/${action}`;
      
      if (action === 'receive') {
        // Open receive modal instead
        const po = purchaseOrders.find(p => p.po_id === poId);
        setSelectedPO(null);
        setReceiveModal(po);
        return;
      }
      
      if (action === 'pay') {
        // Open payment modal instead
        const po = purchaseOrders.find(p => p.po_id === poId);
        setSelectedPO(null);
        setPaymentModal(po);
        return;
      }
      
      await axios.post(endpoint, data, { withCredentials: true });
      
      toast.success(`PO ${action} successful!`);
      loadPurchaseOrders();
      
      // Refresh selected PO if open
      if (selectedPO && selectedPO.po_id === poId) {
        const updatedPO = await axios.get(`${API_BASE}/api/purchase-orders/${poId}`, { withCredentials: true });
        setSelectedPO(updatedPO.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} PO`);
    }
  };
  
  const refreshSelectedPO = async () => {
    if (!selectedPO) return;
    try {
      const res = await axios.get(`${API_BASE}/api/purchase-orders/${selectedPO.po_id}`, { withCredentials: true });
      setSelectedPO(res.data);
    } catch (error) {
      console.error("Error refreshing PO:", error);
    }
  };
  
  // Filter POs
  const filteredPOs = purchaseOrders.filter(po => {
    if (statusFilter !== 'all' && po.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        po.po_number?.toLowerCase().includes(q) ||
        po.vendor?.name?.toLowerCase().includes(q) ||
        po.reason?.description?.toLowerCase().includes(q)
      );
    }
    return true;
  });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="purchase-orders-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Purchase Orders</h2>
          <p className="text-slate-400 text-sm mt-1">Manage vendor orders and track deliveries</p>
        </div>
        <button
          onClick={() => setCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-all"
          data-testid="create-po-btn"
        >
          <Plus className="h-4 w-4" />
          New Purchase Order
        </button>
      </div>
      
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.total_count}</div>
                <div className="text-xs text-slate-500">Total POs</div>
              </div>
            </div>
            <div className="mt-2 text-sm text-slate-400">
              {formatCurrency(stats.total_value)} total value
            </div>
          </div>
          
          <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-amber-400">{stats.pending_count}</div>
                <div className="text-xs text-slate-500">Pending</div>
              </div>
            </div>
            <div className="mt-2 text-sm text-slate-400">
              {formatCurrency(stats.pending_value)} outstanding
            </div>
          </div>
          
          <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                <Package className="h-5 w-5 text-teal-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-teal-400">{stats.received_count}</div>
                <div className="text-xs text-slate-500">Received</div>
              </div>
            </div>
            <div className="mt-2 text-sm text-slate-400">
              {formatCurrency(stats.received_value)} delivered
            </div>
          </div>
          
          <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <Check className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-emerald-400">{stats.paid_count}</div>
                <div className="text-xs text-slate-500">Paid</div>
              </div>
            </div>
            <div className="mt-2 text-sm text-slate-400">
              {formatCurrency(stats.paid_value)} completed
            </div>
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div className="flex items-center gap-4 bg-slate-900/50 rounded-xl border border-slate-800 p-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search POs by number, vendor, or description..."
              className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-500"
              data-testid="po-search-input"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-emerald-500"
            data-testid="po-status-filter"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="sent">Sent</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="partial_received">Partial Received</option>
            <option value="received">Received</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>
      
      {/* PO List */}
      <div className="bg-slate-900/50 rounded-xl border border-slate-800 overflow-hidden">
        {filteredPOs.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 text-slate-700" />
            <h3 className="text-lg font-bold text-white mb-2">
              {purchaseOrders.length === 0 ? 'No Purchase Orders Yet' : 'No Matching POs'}
            </h3>
            <p className="text-slate-500 mb-6">
              {purchaseOrders.length === 0 
                ? 'Create your first purchase order to track vendor orders and deliveries.'
                : 'Try adjusting your search or filter criteria.'}
            </p>
            {purchaseOrders.length === 0 && (
              <button
                onClick={() => setCreateModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-all"
              >
                <Plus className="h-4 w-4" />
                Create Purchase Order
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-800/50">
            {filteredPOs.map((po) => (
              <div 
                key={po.po_id}
                onClick={() => setSelectedPO(po)}
                className="p-4 hover:bg-slate-800/30 cursor-pointer transition-all group"
                data-testid={`po-row-${po.po_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center group-hover:bg-emerald-500/20 transition-all">
                      <FileText className="h-6 w-6 text-slate-400 group-hover:text-emerald-400 transition-all" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-white">{po.po_number}</span>
                        <POStatusBadge status={po.status} />
                      </div>
                      <p className="text-sm text-slate-400">{po.vendor?.name}</p>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-1">{po.reason?.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-white">{formatCurrency(po.total)}</p>
                    <p className="text-xs text-slate-500">{formatDate(po.created_at)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Modals */}
      {createModal && (
        <CreatePOModal
          projectId={projectId}
          milestones={milestones}
          vendors={vendors}
          onClose={() => setCreateModal(false)}
          onCreated={() => {
            setCreateModal(false);
            loadPurchaseOrders();
          }}
        />
      )}
      
      {selectedPO && (
        <PODetailModal
          po={selectedPO}
          onClose={() => setSelectedPO(null)}
          onAction={handlePOAction}
          onRefresh={refreshSelectedPO}
        />
      )}
      
      {receiveModal && (
        <ReceiveItemsModal
          po={receiveModal}
          onClose={() => setReceiveModal(null)}
          onReceived={() => {
            setReceiveModal(null);
            loadPurchaseOrders();
          }}
        />
      )}
      
      {paymentModal && (
        <RecordPOPaymentModal
          po={paymentModal}
          onClose={() => setPaymentModal(null)}
          onPaid={() => {
            setPaymentModal(null);
            loadPurchaseOrders();
          }}
        />
      )}
    </div>
  );
};

export default PurchaseOrdersTab;
